import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Upload, Trash2, Camera, ArrowRight } from 'lucide-react';
import { Controller } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { StarRating } from '@/components/common/StarRating';
import WeightedRatingDisplay from '@/components/Post/WeightedRatingDisplay';
import { DropZone } from '@/components/ui/drop-zone';
import {
  Select,
  SelectContent,
  WideSelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RootState } from '@/redux/store';
import { ICNCBrand, ICNCModel } from '../auth/auth';
import { useMakeUploadForm } from './useMakeForm';
import MachineCard from './MachineSelectCard';
import { RATING_WEIGHTS } from './makeRating';
import { useCreateProjectMakeMutation } from './projectMake.api.slice';
import { ICreateMakeRequest } from './projectMake.d';
import { useGetPreSignedUploadUrlMutation } from '../attachment/attachment.api.slice';
import {
  AttachmentType,
  IAttachmentCategory,
  AttachmentParentType,
} from '../attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import { CacheControl } from '@/lib/s3';

// Interface for populated ownedMachines with nested brand and model objects
interface IPopulatedCNCVariant {
  _id: string;
  name: string;
  cncBrand: ICNCBrand;
  cncModel: ICNCModel;
}

interface UploadMakeModalProps {
  /** Exactly one of postId / projectGroupId. */
  postId?: string;
  projectGroupId?: string;
}

const MAKE_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/avif';

const isMakeImage = (file: File) =>
  MAKE_IMAGE_TYPES.split(',').includes(file.type);

export default function UploadMakeModal({
  postId,
  projectGroupId,
}: UploadMakeModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const [createProjectMake, { isLoading: isCreating }] =
    useCreateProjectMakeMutation();
  const [getPreSignedUploadUrl] = useGetPreSignedUploadUrlMutation();

  const form = useMakeUploadForm();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = form;

  const didCutProject = watch('didCutProject');
  const selectedPrinter = watch('selectedPrinter');

  // Get selected machine data
  const selectedMachine =
    selectedPrinter && user && Array.isArray(user.ownedMachines)
      ? (user.ownedMachines as unknown as IPopulatedCNCVariant[]).find(
          (machine) => machine._id === selectedPrinter
        )
      : null;

  // Auto-select the first machine when modal opens
  useEffect(() => {
    if (
      isOpen &&
      user &&
      Array.isArray(user.ownedMachines) &&
      user.ownedMachines.length > 0 &&
      !selectedPrinter
    ) {
      const firstMachine = (
        user.ownedMachines as unknown as IPopulatedCNCVariant[]
      )[0];
      setValue('selectedPrinter', firstMachine._id, { shouldValidate: true });
    }
  }, [isOpen, user, selectedPrinter, setValue]);

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { files } = event.target;
    if (files) {
      const all = Array.from(files);
      const fileArray = all.filter(isMakeImage);
      setFileError(
        fileArray.length < all.length
          ? 'Only PNG, JPEG, WebP, GIF or AVIF photos can be added.'
          : null
      );
      const newImages = fileArray.map((file) => URL.createObjectURL(file));
      const updatedImages = [...uploadedImages, ...newImages];
      const updatedFiles = [...uploadedFiles, ...fileArray];

      setUploadedImages(updatedImages);
      setUploadedFiles(updatedFiles);
      setValue('images', updatedImages, { shouldValidate: true });
    }
  };

  const uploadFileFromUrlToPresignedUrl = async (
    fileObjectUrl: string,
    presignedUrl: string,
    contentType = ''
  ) => {
    try {
      const fetchResponse = await fetch(fileObjectUrl);

      if (!fetchResponse.ok) {
        const fetchErrorText = await fetchResponse.text();
        throw new Error(
          `Failed to fetch file from ${fileObjectUrl}. Status: ${fetchResponse.status}, ${fetchResponse.statusText}. Details: ${fetchErrorText}`
        );
      }

      const fileContentBlob = await fetchResponse.blob();

      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        body: fileContentBlob,
        headers: {
          'Content-Type': contentType,
          'Cache-Control': CacheControl.IMMUTABLE,
        },
      });

      if (uploadResponse.ok) {
        return {
          status: true,
          message: 'File uploaded successfully',
        };
      }
      throw new Error(
        `Failed to upload file. Status: ${uploadResponse.status}, ${uploadResponse.statusText}`
      );
    } catch (error: any) {
      return {
        status: false,
        message: error.message,
      };
    }
  };

  const removeImage = (index: number) => {
    const updatedImages = uploadedImages.filter((_, i) => i !== index);
    const updatedFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedImages(updatedImages);
    setUploadedFiles(updatedFiles);
    setValue('images', updatedImages, { shouldValidate: true });
  };

  const handleRatingChange = (
    category: keyof typeof RATING_WEIGHTS,
    value: number
  ) => {
    setValue(`ratings.${category}`, value, { shouldValidate: true });
  };

  const onSubmit = async (data: any) => {
    try {
      setIsUploadingFiles(true);

      // First, create the project make
      const makeData: ICreateMakeRequest = {
        postId,
        projectGroupId,
        printedOn: data.selectedPrinter,
        isPrinted: data.didCutProject === 'yes',
        ratings: data.didCutProject === 'yes' ? data.ratings : undefined,
        caption: data.caption || undefined,
      };

      const make = await createProjectMake(makeData).unwrap();

      // Show toast if the submission awards a point to the creator
      if (makeData.isPrinted && makeData.ratings) {
        const totalRating = Object.entries(RATING_WEIGHTS).reduce(
          (sum, [key, weight]) =>
            sum +
            (makeData.ratings![key as keyof typeof RATING_WEIGHTS] ?? 0) *
              weight,
          0
        );
        if (totalRating >= 4.0) {
          const pointsAwarded = import.meta.env.PROD ? 1 : 5;
          toast.success(
            `Your rating awarded the creator ${pointsAwarded} point${pointsAwarded === 1 ? '' : 's'}!`
          );
        }
      }

      // Then upload files if any
      if (uploadedFiles.length > 0) {
        // Generate file hashes and prepare attachment data
        const attachmentPromises = uploadedFiles.map(async (file) => {
          const fileHash = await hashFile(file);
          return {
            parentId: make.make._id,
            parentType: AttachmentParentType.PROJECT_MAKE,
            category: IAttachmentCategory.projectMakeImages,
            fileName: file.name,
            fileHash: fileHash.toString(),
            fileSize: file.size,
            resourceType: AttachmentType.image,
          };
        });

        const attachmentData = await Promise.all(attachmentPromises);

        // Get presigned URLs
        const attachments = await getPreSignedUploadUrl({
          attachments: attachmentData,
        }).unwrap();

        if (attachments.length > 0) {
          // Create a map of fileHash to signedUrl
          const signedUploadUrlsMap = new Map(
            attachments.map((url) => [url.fileHash, url.signedUrl])
          );

          // Upload files to presigned URLs
          const uploadFilePromises = uploadedFiles.map(async (file, index) => {
            const fileHash = await hashFile(file);
            const signedUrl = signedUploadUrlsMap.get(fileHash.toString());
            if (!signedUrl) return Promise.resolve();
            return uploadFileFromUrlToPresignedUrl(
              uploadedImages[index],
              signedUrl,
              file.type
            );
          });

          await Promise.all(uploadFilePromises);
        }
      }

      setIsOpen(false);
      form.reset();
      setUploadedImages([]);
      setUploadedFiles([]);
      setFileError(null);
      // Clear the file input
      const fileInput = document.getElementById(
        'make-images'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error: any) {
      const status = error?.status ?? error?.data?.status;
      if (status === 409) {
        toast.error('You have already submitted a make for this project.');
      } else {
        toast.error('Failed to submit make. Please try again.');
      }
    } finally {
      setIsUploadingFiles(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    form.reset();
    setUploadedImages([]);
  };

  if (!user) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <></>;
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {/* An invitation, not a secondary button — makes are the point of this section. */}
        <button
          type="button"
          className="group w-full text-left rounded-md border border-border p-4 flex items-center gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Camera className="w-5 h-5 shrink-0 text-primary" />
          <span className="flex-1 min-w-0">
            <span className="block font-primary font-bold text-base">
              Cut this one? Share your Make
            </span>
            <span className="block text-sm text-muted-foreground">
              Post a photo of your finished piece
            </span>
          </span>
          <ArrowRight className="w-5 h-5 shrink-0 text-primary transition-transform group-hover:translate-x-1" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between font-medium text-lg">
            Upload Make
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Images</Label>
            <DropZone
              accept={MAKE_IMAGE_TYPES}
              onDrop={handleImageUpload}
              className="h-24 flex flex-col items-center justify-center p-2"
              dragActiveClassName="border-blue-500 bg-blue-50"
            >
              <Upload className="w-6 h-6 text-gray-400 mb-1" />
              <span className="text-xs text-gray-500">Add Image</span>
            </DropZone>
            <input
              id="make-images"
              type="file"
              accept={MAKE_IMAGE_TYPES}
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
            {fileError && (
              <p role="alert" className="text-sm text-red-500">
                {fileError}
              </p>
            )}
            {errors.images && (
              <p className="text-sm text-red-500">{errors.images.message}</p>
            )}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {uploadedImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => removeImage(index)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-3 w-full min-w-0">
            <Label className="text-sm font-medium">Machine Created On</Label>
            {!user || user?.ownedMachines.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground border border-gray-200 rounded-md">
                <p>
                  No machines available. Please add machines to your profile
                  {user && user.username ? (
                    <>
                      <br />
                      <Link
                        to={`/${user.username}?addMachine=true`}
                        className="text-blue-500"
                      >
                        click here to add machines
                      </Link>
                    </>
                  ) : (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <></>
                  )}
                  .
                </p>
              </div>
            ) : (
              <Controller
                name="selectedPrinter"
                control={control}
                render={({ field }) => (
                  <div className="space-y-3 w-full min-w-0 border border-gray-200 p-1 rounded-md">
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-auto p-0 border-0 w-full min-w-0 flex-1">
                        <SelectValue placeholder="Select your machine">
                          {selectedMachine ? (
                            <MachineCard
                              brand={selectedMachine.cncBrand.name}
                              model={selectedMachine.cncModel.name}
                              variant={selectedMachine.name}
                              iconSize="sm"
                            />
                          ) : (
                            'Select your machine'
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent className="max-h-96 w-full min-w-0 p-0 flex flex-col gap-3">
                        {(
                          user?.ownedMachines as unknown as IPopulatedCNCVariant[]
                        )?.map((machine) => (
                          <WideSelectItem
                            key={machine._id}
                            value={machine._id}
                            className="p-0 w-full my-2"
                          >
                            <MachineCard
                              brand={machine.cncBrand.name}
                              model={machine.cncModel.name}
                              variant={machine.name}
                              iconSize="sm"
                            />
                          </WideSelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              />
            )}
            {errors.selectedPrinter && (
              <p className="text-sm text-red-500">
                {errors.selectedPrinter.message}
              </p>
            )}
          </div>

          <div className="space-y-3">
            <Label>Did You Cut This Project?</Label>
            <Controller
              name="didCutProject"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2 w-40 border border-[#D6D6D6] h-[40px] py-2 px-3 rounded-md cursor-pointer">
                    <RadioGroupItem value="yes" id="cut-yes" />
                    <Label
                      htmlFor="cut-yes"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                    >
                      Yes
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 w-40 border border-[#D6D6D6] h-[40px] py-2 px-3 rounded-md cursor-pointer">
                    <RadioGroupItem value="no" id="cut-no" />
                    <Label
                      htmlFor="cut-no"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex-1 cursor-pointer"
                    >
                      No
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {errors.didCutProject && (
              <p className="text-sm text-red-500">
                {errors.didCutProject.message}
              </p>
            )}
            {didCutProject === 'no' && (
              <p className="text-sm text-gray-600 italic">
                Skip the rest (no rating allowed)
              </p>
            )}
          </div>

          {didCutProject === 'yes' && (
            <div className="space-y-6">
              <div>
                <Label>Rating Criteria</Label>
              </div>

              <div className="space-y-4">
                <div className="p-4 border rounded-lg">
                  <StarRating
                    rating={watch('ratings.cutExpectation') || 0}
                    onRatingChange={(rating) =>
                      handleRatingChange('cutExpectation', rating)
                    }
                    label="Cut Expectation"
                    description="Did the actual result match your expectations from previews or renders?"
                  />
                </div>

                <div className="p-4 border rounded-lg">
                  <StarRating
                    rating={watch('ratings.cutQuality') || 0}
                    onRatingChange={(rating) =>
                      handleRatingChange('cutQuality', rating)
                    }
                    label="Cut Quality"
                    description="How cleanly and accurately did the file cut on your machine?"
                  />
                </div>

                <div className="p-4 border rounded-lg">
                  <StarRating
                    rating={watch('ratings.correctFileInfo') || 0}
                    onRatingChange={(rating) =>
                      handleRatingChange('correctFileInfo', rating)
                    }
                    label="Correct File Information"
                    description="Were toolpaths, materials, and settings clearly documented and accurate?"
                  />
                </div>

                <div className="p-4 border rounded-lg">
                  <StarRating
                    rating={watch('ratings.coolFactor') || 0}
                    onRatingChange={(rating) =>
                      handleRatingChange('coolFactor', rating)
                    }
                    label="Cool Factor"
                    description="Is the design visually impressive, useful, or just plain awesome?"
                  />
                </div>
              </div>

              {errors.ratings && (
                <p className="text-sm text-red-500">{errors.ratings.message}</p>
              )}
              <WeightedRatingDisplay
                ratings={watch('ratings') as unknown as Record<string, number>}
                weights={RATING_WEIGHTS}
              />
            </div>
          )}

          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption">Caption</Label>
            <Controller
              name="caption"
              control={control}
              render={({ field }) => (
                <Textarea
                  id="caption"
                  placeholder="Share your experience with this project..."
                  {...field}
                  rows={4}
                />
              )}
            />
          </div>

          <div className="flex gap-3 pt-4 flex-col">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={
                !isValid ||
                isCreating ||
                isUploadingFiles ||
                user?.ownedMachines.length === 0
              }
            >
              {isCreating || isUploadingFiles
                ? 'Publishing...'
                : 'Publish make'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
